import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";

export default function RegisterPage() {
  const { token } = useParams();
  const { register } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}/verify`);
        setValid(true);
        setForm((f) => ({
          ...f,
          name: data.name || "",
          email: data.email || "",
        }));
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        invite_token: token,
      });
      toast.success(t("auth.account_created"));
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-8">
      <div className="w-full max-w-md">
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mb-6 text-center">
          {t("auth.join")}
        </h1>

        {verifying && (
          <BrutalCard>
            <p className="font-medium text-center">{t("auth.verifying")}</p>
          </BrutalCard>
        )}

        {!verifying && !valid && (
          <BrutalCard data-testid="invalid-invite">
            <h2 className="text-xl font-semibold mb-2">{t("auth.invite_invalid_title")}</h2>
            <p className="text-sm">{error || t("auth.invite_invalid_body")}</p>
            <BrutalButton variant="secondary" className="mt-6 w-full" onClick={() => nav("/login")} data-testid="register-go-login">
              {t("auth.go_login")}
            </BrutalButton>
          </BrutalCard>
        )}

        {!verifying && valid && (
          <BrutalCard data-testid="register-card">
            <h2 className="text-xl font-semibold mb-2">{t("auth.create_account")}</h2>
            <p className="text-sm text-zinc-500 mb-6">
              {t("auth.invited_as_distributor")}
            </p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">{t("auth.full_name")}</label>
                <BrutalInput
                  data-testid="register-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t("common.email")}</label>
                <BrutalInput
                  data-testid="register-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t("common.password")}</label>
                <BrutalInput
                  data-testid="register-password-input"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <BrutalButton
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full"
                data-testid="register-submit-button"
              >
                {loading ? t("auth.creating") : t("auth.create")}
              </BrutalButton>
            </form>
          </BrutalCard>
        )}
      </div>
    </div>
  );
}
